import { z } from "zod";
import { type TFunction } from "i18next";

export type CompanyGeneralFormValues = {
    name?: string;
    email?: string;
    country?: string;
    zip?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    phone?: string;
    vat?: string;
};

export const createCompanyGeneralFormSchema = (isPaymentEnabled: boolean, t: TFunction) =>
    z.object({
        name:
            isPaymentEnabled ?
                z
                    .string()
                    .trim()
                    .min(2, { message: t("companySettings.form.validation.name.required", "Company name is required") })
                    .max(255, {
                        message: t(
                            "companySettings.form.validation.name.maxLength",
                            "Company name must be 255 characters or fewer",
                        ),
                    })
            :   z
                    .string()
                    .trim()
                    .max(255, {
                        message: t(
                            "companySettings.form.validation.name.maxLength",
                            "Company name must be 255 characters or fewer",
                        ),
                    })
                    .optional(),
        email:
            isPaymentEnabled ?
                z
                    .string()
                    .trim()
                    .email({ message: t("companySettings.form.validation.email.invalid", "Invalid email address") })
                    .max(254, {
                        message: t(
                            "companySettings.form.validation.email.maxLength",
                            "Email must be 254 characters or fewer",
                        ),
                    })
            :   z
                    .string()
                    .trim()
                    .email({ message: t("companySettings.form.validation.email.invalid", "Invalid email address") })
                    .max(254, {
                        message: t(
                            "companySettings.form.validation.email.maxLength",
                            "Email must be 254 characters or fewer",
                        ),
                    })
                    .optional(),
        country:
            isPaymentEnabled ?
                z
                    .string()
                    .trim()
                    .min(2, { message: t("companySettings.form.validation.country.required", "Country is required") })
                    .regex(/^[A-Za-zÀ-ÿ\s.'-]+$/u, {
                        message: t(
                            "companySettings.form.validation.country.invalid",
                            "Country must contain only letters and spaces",
                        ),
                    })
                    .max(255, {
                        message: t(
                            "companySettings.form.validation.country.maxLength",
                            "Country must be 255 characters or fewer",
                        ),
                    })
            :   z
                    .string()
                    .trim()
                    .regex(/^[A-Za-zÀ-ÿ\s.'-]+$/u, {
                        message: t(
                            "companySettings.form.validation.country.invalid",
                            "Country must contain only letters and spaces",
                        ),
                    })
                    .max(255, {
                        message: t(
                            "companySettings.form.validation.country.maxLength",
                            "Country must be 255 characters or fewer",
                        ),
                    })
                    .optional(),
        zip:
            isPaymentEnabled ?
                z
                    .string()
                    .trim()
                    .min(2, {
                        message: t("companySettings.form.validation.zip.required", "Zip/Postal code is required"),
                    })
                    .regex(/^[A-Za-z0-9][A-Za-z0-9\- ]{1,10}$/u, {
                        message: t("companySettings.form.validation.zip.invalid", "Invalid zip/postal code"),
                    })
                    .max(12, {
                        message: t(
                            "companySettings.form.validation.zip.maxLength",
                            "Zip/Postal code must be 12 characters or fewer",
                        ),
                    })
            :   z
                    .string()
                    .trim()
                    .regex(/^[A-Za-z0-9][A-Za-z0-9\- ]{1,10}$/u, {
                        message: t("companySettings.form.validation.zip.invalid", "Invalid zip/postal code"),
                    })
                    .max(12, {
                        message: t(
                            "companySettings.form.validation.zip.maxLength",
                            "Zip/Postal code must be 12 characters or fewer",
                        ),
                    })
                    .optional(),
        address1: z
            .string()
            .trim()
            .max(255, {
                message: t(
                    "companySettings.form.validation.address1.maxLength",
                    "Address 1 must be 255 characters or fewer",
                ),
            })
            .optional(),
        address2: z
            .string()
            .trim()
            .max(255, {
                message: t(
                    "companySettings.form.validation.address2.maxLength",
                    "Address 2 must be 255 characters or fewer",
                ),
            })
            .optional(),
        city: z
            .string()
            .trim()
            .max(255, {
                message: t("companySettings.form.validation.city.maxLength", "City must be 255 characters or fewer"),
            })
            .optional(),
        state: z
            .string()
            .trim()
            .max(255, {
                message: t("companySettings.form.validation.state.maxLength", "State must be 255 characters or fewer"),
            })
            .optional(),
        phone: z
            .string()
            .trim()
            .max(32, {
                message: t(
                    "companySettings.form.validation.phone.maxLength",
                    "Phone number must be 32 characters or fewer",
                ),
            })
            .regex(/^[0-9+().\-\s]*$/u, {
                message: t("companySettings.form.validation.phone.invalid", "Invalid phone number"),
            })
            .optional(),
        vat: z
            .string()
            .trim()
            .max(255, {
                message: t(
                    "companySettings.form.validation.vat.maxLength",
                    "International Registration Number must be 255 characters or fewer",
                ),
            })
            .optional(),
    });
